import React, { useState, useEffect } from 'react';
import { Plus, Trash2, ExternalLink, Package, TrendingUp, AlertTriangle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import apiClient from '../../api/client';

const OpportunityParts = ({ opportunityId, opportunity, onRevenueUpdate, onMismatchUpdate }) => {
  const [parts, setParts] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedPartId, setSelectedPartId] = useState('');
  const [linking, setLinking] = useState(false);
  const [creating, setCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [mismatchCount, setMismatchCount] = useState(0);
  const [expectedStatus, setExpectedStatus] = useState('');
  
  // Helper to map opportunity stage to part business status
  const getBusinessStatusFromStage = (stage) => {
    const stageMap = {
      'target': 'In Progress',
      'qualified': 'In Progress',
      'rfq': 'Quoted',
      'quoted': 'Quoted',
      'awarded': 'Awarded',
      'sourced': 'Awarded',
      'lost': 'Lost'
    };
    return stageMap[stage] || 'Quoted';
  };

  // Helper to determine production country based on region
  const getProductionCountryFromRegion = (region) => {
    if (!region) return 'US';
    const regionLower = region.toLowerCase();
    if (regionLower.includes('north america')) return 'US';
    if (regionLower.includes('mexico')) return 'MX';
    if (regionLower.includes('china') || regionLower.includes('asia')) return 'CN';
    if (regionLower.includes('europe')) return 'DE';
    return 'US';
  };

  const [newPartData, setNewPartData] = useState({
    part_number: '',
    description: '',
    oem: '',
    carline: '',
    production_country: 'US',
    destination_country: 'US',
    business_status: 'Quoted'
  });

  useEffect(() => {
    if (opportunityId) {
      loadLinkedParts();
    }
  }, [opportunityId]);

  // Debounced search - only search after user stops typing for 300ms
  useEffect(() => {
    if (showLinkDialog) {
      // Clear previous timeout
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }

      // Don't search if less than 2 characters
      if (searchTerm.length < 2) {
        setSearchResults([]);
        return;
      }

      // Set new timeout for search
      const timeout = setTimeout(() => {
        searchParts(searchTerm);
      }, 300);

      setSearchTimeout(timeout);

      // Cleanup
      return () => clearTimeout(timeout);
    }
  }, [searchTerm, showLinkDialog]);

  const loadLinkedParts = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/v1/pipeline/opportunities/${opportunityId}/parts`);
      if (response.data.success) {
        setParts(response.data.data);
        // Update mismatch tracking
        setMismatchCount(response.data.mismatch_count || 0);
        setExpectedStatus(response.data.expected_part_status || '');
        // Notify parent component of mismatch data
        if (onMismatchUpdate) {
          onMismatchUpdate({
            mismatch_count: response.data.mismatch_count || 0,
            expected_part_status: response.data.expected_part_status || '',
            total_parts: response.data.total_parts || 0
          });
        }
      }
    } catch (error) {
      console.error('Error loading parts:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchParts = async (query) => {
    try {
      setSearching(true);
      const response = await apiClient.get('/api/v1/pipeline/parts/search', { 
        params: { q: query, limit: 20 } 
      });
      
      if (response.data.success) {
        setSearchResults(response.data.data || []);
      }
    } catch (error) {
      console.error('Error searching parts:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleLinkPart = async () => {
    if (!selectedPartId) {
      alert('Please select a part');
      return;
    }

    try {
      setLinking(true);
      const response = await apiClient.post(
        `/api/v1/pipeline/opportunities/${opportunityId}/parts?part_id=${selectedPartId}&link_type=reference`
      );

      if (response.data.success) {
        await loadLinkedParts();
        setShowLinkDialog(false);
        setSelectedPartId('');
        setSearchTerm('');
        setSearchResults([]);
        
        // Backend automatically calculated revenue - update parent
        if (response.data.revenue_updated && onRevenueUpdate) {
          onRevenueUpdate({
            total_revenue_cents: response.data.new_revenue_cents,
            linked_parts_count: parts.length + 1
          });
        }
      } else {
        alert(response.data.error || 'Failed to link part');
      }
    } catch (error) {
      console.error('Error linking part:', error);
      alert(error.response?.data?.detail || 'Failed to link part');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkPart = async (linkId, partNumber) => {
    if (!window.confirm(`Unlink part ${partNumber}?`)) return;

    try {
      const response = await apiClient.delete(
        `/api/v1/pipeline/opportunities/${opportunityId}/parts/${linkId}`
      );

      if (response.data.success) {
        await loadLinkedParts();
        
        // Backend automatically calculated revenue - update parent
        if (response.data.revenue_updated && onRevenueUpdate) {
          onRevenueUpdate({
            total_revenue_cents: response.data.new_revenue_cents,
            linked_parts_count: parts.length - 1
          });
        }
      } else {
        alert('Failed to unlink part');
      }
    } catch (error) {
      console.error('Error unlinking part:', error);
      alert('Failed to unlink part');
    }
  };

  const handleCreatePart = async () => {
    if (!newPartData.part_number || !newPartData.description) {
      alert('Part number and description are required');
      return;
    }

    try {
      setCreating(true);
      const response = await apiClient.post(
        `/api/v1/pipeline/opportunities/${opportunityId}/create-part`,
        newPartData
      );

      if (response.data.success) {
        await loadLinkedParts();
        setShowCreateDialog(false);
        setNewPartData({
          part_number: '',
          description: '',
          oem: '',
          carline: '',
          production_country: 'US',
          destination_country: 'US',
          business_status: 'Quoted'
        });
        
        // Backend automatically calculated revenue - update parent
        if (response.data.revenue_updated && onRevenueUpdate) {
          onRevenueUpdate({
            total_revenue_cents: response.data.new_revenue_cents,
            linked_parts_count: parts.length + 1
          });
        }
        
        alert('Part created and linked successfully!');
      } else {
        alert(response.data.error || 'Failed to create part');
      }
    } catch (error) {
      console.error('Error creating part:', error);
      alert(error.response?.data?.detail || 'Failed to create part');
    } finally {
      setCreating(false);
    }
  };

  const formatCurrency = (cents) => {
    if (!cents) return '$0';
    const dollars = cents / 100;
    if (dollars >= 1000000) {
      return `$${(dollars / 1000000).toFixed(1)}M`;
    }
    return `$${dollars.toLocaleString()}`;
  };

  // Get display price based on business status
  // Quoting stage → Show quoted_price_cents (or "No quote" if empty)
  // Awarded/Won → Show awarded_price_cents
  // Always show target_price_cents as secondary
  const getDisplayPrice = (part) => {
    const isAwarded = ['Nominated', 'Awarded', 'Development', 'Pre-Production', 'Production'].includes(part.business_status);
    
    if (isAwarded && part.awarded_price_cents) {
      return {
        primary: formatCurrency(part.awarded_price_cents),
        secondary: part.target_price_cents ? `Target: ${formatCurrency(part.target_price_cents)}` : null,
        label: 'Awarded'
      };
    } else if (part.quoted_price_cents) {
      return {
        primary: formatCurrency(part.quoted_price_cents),
        secondary: part.target_price_cents ? `Target: ${formatCurrency(part.target_price_cents)}` : null,
        label: 'Quoted'
      };
    } else {
      return {
        primary: part.target_price_cents ? formatCurrency(part.target_price_cents) : null,
        secondary: null,
        label: part.target_price_cents ? 'Target' : null,
        noQuote: !part.target_price_cents
      };
    }
  };

  // Filter out already linked parts from search results
  const availableParts = searchResults.filter(part => {
    const alreadyLinked = parts.some(p => p.part_id === part.id);
    return !alreadyLinked;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading parts...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Linked Parts</h3>
          <p className="text-xs text-gray-500 mt-1">
            {parts.length} part{parts.length !== 1 ? 's' : ''} linked to this opportunity
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowLinkDialog(true)}
            className="px-3 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Link Existing
          </button>
          <button
            onClick={() => {
              // Pre-populate form with opportunity data
              setNewPartData({
                part_number: '',
                description: '',
                oem: opportunity?.oem || '',
                carline: opportunity?.platform_code || opportunity?.program_name || '',
                production_country: getProductionCountryFromRegion(opportunity?.region) || 'US',
                destination_country: opportunity?.country || 'US',
                business_status: getBusinessStatusFromStage(opportunity?.stage) || 'Quoted'
              });
              setShowCreateDialog(true);
            }}
            className="px-3 py-2 text-sm text-white bg-green-600 hover:bg-green-700 rounded flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Create New
          </button>
        </div>
      </div>

      {/* Mismatch Summary */}
      {mismatchCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            <strong>{mismatchCount}</strong> part{mismatchCount > 1 ? 's have' : ' has'} status that differs from expected &ldquo;{expectedStatus}&rdquo; for this opportunity stage.
          </p>
        </div>
      )}

      {/* Parts List */}
      {parts.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-sm text-gray-600 mb-1">No parts linked yet</p>
          <p className="text-xs text-gray-500">
            Link existing parts to track detailed forecasts and tariff analysis
          </p>
        </div>
      ) : (
        <TooltipProvider>
          <div className="space-y-2">
            {parts.map((part) => (
              <div
                key={part.link_id}
                className={`bg-white border rounded-lg p-4 hover:border-gray-300 transition-colors ${
                  part.status_mismatch ? 'border-amber-300 bg-amber-50/30' : 'border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Package className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-gray-900">{part.part_number}</span>
                      {part.business_status && (
                        <span className={`px-2 py-0.5 text-xs rounded ${
                          part.status_mismatch 
                            ? 'bg-amber-100 text-amber-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {part.business_status}
                        </span>
                      )}
                      {part.status_mismatch && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-700 cursor-help">
                              <AlertTriangle className="w-3 h-3" />
                              Mismatch
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p>Part status ({part.actual_status}) differs from expected ({part.expected_status}) for this opportunity stage.</p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2">{part.description}</p>
                    
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>{part.oem}</span>
                      {part.carline && (
                        <>
                          <span>•</span>
                          <span>{part.carline}</span>
                        </>
                      )}
                      {part.total_volume > 0 && (
                        <>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" />
                            {part.total_volume.toLocaleString()} units
                          </span>
                        </>
                      )}
                      {/* Smart Price Display */}
                      {(() => {
                        const priceInfo = getDisplayPrice(part);
                        if (priceInfo.noQuote) {
                          return (
                            <>
                              <span>•</span>
                              <span className="text-amber-600 italic">No quote entered</span>
                            </>
                          );
                        }
                        return (
                          <>
                            <span>•</span>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="flex items-center gap-1 cursor-help">
                                  <span className={priceInfo.label === 'Awarded' ? 'text-green-600 font-medium' : ''}>
                                    {priceInfo.primary}
                                  </span>
                                  <span className="text-gray-400 text-[10px]">({priceInfo.label})</span>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <div className="text-xs">
                                  {priceInfo.secondary && <div>{priceInfo.secondary}</div>}
                                  {part.price_locked && <div className="text-amber-600">Price locked</div>}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <a
                      href={`/parts/${part.part_number}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                    title="View part details"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <button
                    onClick={() => handleUnlinkPart(part.link_id, part.part_number)}
                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                    title="Unlink part"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          </div>
        </TooltipProvider>
      )}

      {/* Link Part Dialog */}
      {showLinkDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Link Existing Part</h2>
              <p className="text-sm text-gray-500 mt-1">
                Select a part from your existing parts to link to this opportunity
              </p>
            </div>

            {/* Search */}
            <div className="px-6 py-4 border-b border-gray-200">
              <input
                type="text"
                placeholder="Type to search parts (min 2 characters)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {searching && (
                <p className="text-xs text-gray-500 mt-2">Searching...</p>
              )}
              {searchTerm.length > 0 && searchTerm.length < 2 && (
                <p className="text-xs text-gray-500 mt-2">Type at least 2 characters to search</p>
              )}
            </div>

            {/* Parts List */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {searchTerm.length < 2 ? (
                <div className="text-center py-12 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">Start typing to search</p>
                  <p className="text-sm mt-2">Search by part number, description, OEM, or carline</p>
                  <p className="text-xs mt-2 text-gray-400">Results limited to 20 for fast performance</p>
                </div>
              ) : searching ? (
                <div className="text-center py-12 text-gray-500">
                  <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-3"></div>
                  <p>Searching parts...</p>
                </div>
              ) : availableParts.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No parts found</p>
                  <p className="text-sm mt-2">Try a different search term</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 mb-3">
                    Found {availableParts.length} part{availableParts.length !== 1 ? 's' : ''}
                  </p>
                  {availableParts.map((part) => (
                    <label
                      key={part.id}
                      className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedPartId === part.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="part"
                        value={part.id}
                        checked={selectedPartId === part.id}
                        onChange={() => setSelectedPartId(part.id)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{part.part_number}</div>
                        <div className="text-sm text-gray-600">{part.description}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {part.oem} {part.carline && `• ${part.carline}`}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowLinkDialog(false);
                  setSelectedPartId('');
                  setSearchTerm('');
                  setSearchResults([]);
                }}
                disabled={linking}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleLinkPart}
                disabled={!selectedPartId || linking}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {linking ? 'Linking...' : 'Link Part'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create New Part Dialog */}
      {showCreateDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Create New Part</h2>
              <p className="text-sm text-gray-500 mt-1">
                Create a new part and link it to this opportunity
              </p>
            </div>

            {/* Form */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Part Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newPartData.part_number}
                  onChange={(e) => setNewPartData({ ...newPartData, part_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., PN-5001"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newPartData.description}
                  onChange={(e) => setNewPartData({ ...newPartData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Steering Wheel Assembly"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    OEM
                  </label>
                  <input
                    type="text"
                    value={newPartData.oem}
                    onChange={(e) => setNewPartData({ ...newPartData, oem: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-blue-50"
                    placeholder={`Pre-filled: ${opportunity?.oem || 'N/A'}`}
                  />
                  {newPartData.oem && (
                    <p className="text-xs text-blue-600 mt-1">Pre-populated from opportunity</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Carline/Program
                  </label>
                  <input
                    type="text"
                    value={newPartData.carline}
                    onChange={(e) => setNewPartData({ ...newPartData, carline: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-blue-50"
                    placeholder={`Pre-filled: ${opportunity?.platform_code || opportunity?.program_name || 'N/A'}`}
                  />
                  {newPartData.carline && (
                    <p className="text-xs text-blue-600 mt-1">Pre-populated from opportunity</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Production Country
                  </label>
                  <select
                    value={newPartData.production_country}
                    onChange={(e) => setNewPartData({ ...newPartData, production_country: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-blue-50"
                  >
                    <option value="US">US</option>
                    <option value="MX">Mexico</option>
                    <option value="CN">China</option>
                    <option value="CA">Canada</option>
                    <option value="DE">Germany</option>
                    <option value="JP">Japan</option>
                    <option value="KR">South Korea</option>
                  </select>
                  <p className="text-xs text-blue-600 mt-1">Inferred from opportunity region</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Destination Country
                  </label>
                  <select
                    value={newPartData.destination_country}
                    onChange={(e) => setNewPartData({ ...newPartData, destination_country: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-blue-50"
                  >
                    <option value="US">US</option>
                    <option value="MX">Mexico</option>
                    <option value="CN">China</option>
                    <option value="CA">Canada</option>
                    <option value="EU">Europe</option>
                  </select>
                  <p className="text-xs text-blue-600 mt-1">Pre-populated from opportunity</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Status
                </label>
                <select
                  value={newPartData.business_status}
                  onChange={(e) => setNewPartData({ ...newPartData, business_status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-blue-50"
                >
                  <option value="Quoted">Quoted</option>
                  <option value="Awarded">Awarded</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Lost">Lost</option>
                </select>
                <p className="text-xs text-blue-600 mt-1">Based on opportunity stage: {opportunity?.stage || 'N/A'}</p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-xs text-blue-800">
                  <strong>✨ Smart Pre-fill:</strong> Fields are automatically populated from the opportunity context 
                  (OEM, Program, Routes, Status). SOP date will be inherited. You can modify any field as needed.
                  The part will be automatically linked to this opportunity upon creation.
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateDialog(false);
                  setNewPartData({
                    part_number: '',
                    description: '',
                    oem: '',
                    carline: '',
                    production_country: 'US',
                    destination_country: 'US',
                    business_status: 'Quoted'
                  });
                }}
                disabled={creating}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreatePart}
                disabled={!newPartData.part_number || !newPartData.description || creating}
                className="px-4 py-2 text-sm text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creating ? 'Creating...' : 'Create & Link Part'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpportunityParts;
